import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import useClassy from '@core/hooks/useClassy';
import { useProjectStore } from '@core/store';

import Button from '@ui/Button';
import RDMD from '@ui/RDMD';

import { TutorialProp } from '../Modal/proptypes/tutorials';

import classes from './style.module.scss';

const syntaxHighlighter = typeof window !== 'undefined' ? require('@readme/syntax-highlighter/dist').default : () => {};

const TutorialHero = ({ hero, openTutorial }) => {
  const bem = useClassy(classes, 'TutorialHero');
  const { t } = useTranslation();
  const isRmdx = useProjectStore(s => s.data.features.mdx === 'enabled');
  return (
    <section className={bem('&', 'rm-RecipeHero')}>
      <div className={bem('-Col')}>
        <strong className={bem('-Col-Title')}>{hero.title}</strong>
        <span className={bem('rm-Markdown', 'markdown-body', '-Col-Description')}>
          <RDMD renderer={isRmdx ? 'rmdx' : 'rdmd'}>{hero.description}</RDMD>
        </span>
        {!!hero.steps && hero.steps.length > 0 && (
          <>
            <div className={bem('-Col-Heading', '-Col-Heading_uppercase')}>{t('recipes.inThisRecipe')}</div>
            <ol className={bem('-Col-List')}>
              {hero.steps.slice(0, 3).map((step, i) => {
                return (
                  <li key={i} className={bem('-Col-List-Item')}>
                    {step.title}
                  </li>
                );
              })}
            </ol>
          </>
        )}
        {!!hero.steps && hero.steps.length > 3 && (
          <button className={bem('-Col-Ellipsis')} type="button">
            •••
            <div className={bem('-Col-Ellipsis-Popup')}>
              <ol className={bem('-Col-List', '-Col-List_ellipsis')}>
                {hero.steps.slice(3).map((step, i) => {
                  return (
                    <li key={i} className={bem('-Col-List-Item')}>
                      {step.title}
                    </li>
                  );
                })}
              </ol>
            </div>
          </button>
        )}
        <Button
          className={bem('-Col-Button', 'rm-RecipeHero-button')}
          onClick={() => openTutorial('View', hero)}
          size="lg"
        >
          {!!hero.emoji && <span className={bem('-Col-Button-Emoji')}>{hero.emoji}</span>}
          <span>{t('recipes.openRecipe')}</span>
        </Button>
      </div>
      <div className={bem('-Image')}>
        <div className={bem('-Image-TutorialEditor-Nav')}>
          {!!hero.snippet &&
            hero.snippet.codeOptions.map(({ name }) => (
              <Button key={name} bem={{ white_text: true }} className={bem('-Image-TutorialEditor-Nav-Button')}>
                {name}
              </Button>
            ))}
        </div>
        {/*
        CodeMirror has a textarea that needs a label. Technically…not more accessible this way, but it does pass WCAG tests.
        Supposedly this is resolved if we upgrade CodeMirror, but have not confirmed.
       */}
        <label>
          {syntaxHighlighter(
            hero.snippet ? hero.snippet.codeOptions[0].code : '',
            hero.snippet ? hero.snippet.codeOptions[0].highlightedSyntax : 'javascript',
            {
              dark: true,
              highlightMode: true,
              noCursor: true,
              readOnly: true,
              tokenizeVariables: true,
              foldGutter: true,
            },
            { className: 'CodeEditor-Input CodeEditor-Input_readonly' },
          )}
        </label>
      </div>
    </section>
  );
};

TutorialHero.propTypes = {
  hero: TutorialProp.isRequired,
  openTutorial: PropTypes.func.isRequired,
};

export default TutorialHero;
